import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../lib/api";
import { MaskedLines, FadeUp, SectionHead } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { usePageTitle } from "../hooks/usePageTitle";

const MuralShot = ({ file, alt, testId }) => (
  <div className="relative border border-white/10 overflow-hidden group" data-testid={testId}>
    <img
      src={imgUrl(`/api/uploads/murals/${file}`)}
      alt={alt}
      loading="lazy"
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      className="w-full h-auto block artwork-view transition-transform duration-500 group-hover:scale-[1.02]"
      data-testid={`${testId}-image`}
    />
    <span className="absolute bottom-3 right-3 bg-ink/85 backdrop-blur-sm border border-pink/50 text-pink text-[9px] font-extrabold tracking-[0.3em] px-3 py-1.5" data-testid={`${testId}-badge`}>
      CONCEPT VISUALIZATION
    </span>
  </div>
);

const COMMERCIAL_SPACES = ["RESTAURANTS", "BARS", "RETAIL STORES", "BOUTIQUES", "HOTELS", "OFFICES", "GYMS", "ENTERTAINMENT VENUES", "EVENT SPACES", "EXTERIOR COMMERCIAL WALLS"];
const RESIDENTIAL_SPACES = ["HOME OFFICES", "GAME ROOMS", "MEDIA ROOMS", "BEDROOMS", "GARAGES", "HOME GYMS", "ENTERTAINMENT ROOMS", "STATEMENT WALLS"];

const ConceptFrame = ({ label, testId }) => (
  <div className="border border-dashed border-white/20 aspect-[4/3] flex flex-col items-center justify-center gap-3 p-4" data-testid={testId}>
    <span className="text-[10px] tracking-[0.3em] text-smoke text-center leading-relaxed">{label}</span>
    <span className="bg-charcoal border border-pink/50 text-pink text-[9px] font-extrabold tracking-[0.3em] px-3 py-1.5">CONCEPT VISUALIZATION</span>
  </div>
);

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-2 mt-5">
    {items.map((c) => (
      <span key={c} className="text-[10px] font-bold tracking-[0.2em] border border-white/15 text-bone/70 px-3 py-2">{c}</span>
    ))}
  </div>
);

const inputCls = "w-full h-12 bg-charcoal border border-white/15 px-4 text-sm outline-none focus:border-pink placeholder:text-smoke/60";
const labelCls = "block text-[10px] font-bold tracking-[0.25em] text-smoke mb-2";

export default function Murals() {
  usePageTitle("Custom Murals — Commercial & Residential");
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    project_type: params.get("type") === "residential" ? "Residential" : "Commercial",
    project_location: "", indoor_outdoor: "", wall_dimensions: "",
    style_concept: "", budget: "", completion_date: "",
  });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      await api.post("/inquiries/mural", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Quote requested — Kenny will be in touch.");
      setForm({ ...form, name: "", email: "", phone: "", project_location: "", wall_dimensions: "", style_concept: "", budget: "", completion_date: "" });
      setPhoto(null);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pb-20" data-testid="murals-page">
      <PageHero slug="piece-05" testId="murals-hero">
        <p className="text-[10px] font-bold tracking-[0.3em] text-smoke mb-4">CUSTOM MURALS — COMMERCIAL + RESIDENTIAL</p>
        <h1 className="font-display leading-[0.88] tracking-wide" data-testid="murals-headline">
          <MaskedLines
            lines={[
              <span key="a" className="text-[15vw] sm:text-8xl">YOUR WALL</span>,
              <span key="b" className="text-[15vw] sm:text-8xl text-outline">SHOULD SAY</span>,
              <span key="c" className="text-[15vw] sm:text-8xl">SOMETHING<span className="text-pink">.</span></span>,
            ]}
          />
        </h1>
        <FadeUp delay={0.5} className="mt-6 max-w-md">
          <p className="text-base text-bone/80 leading-relaxed" data-testid="murals-subcopy">
            Custom artwork created for homes, businesses and spaces that weren't built to blend in.
          </p>
        </FadeUp>
        <FadeUp delay={0.6} className="mt-8 flex flex-wrap gap-3">
          <a href="#commercial" data-testid="goto-commercial" className="h-12 px-6 bg-bone text-ink text-xs font-extrabold tracking-[0.18em] flex items-center gap-2 hover:bg-pink hover:text-white transition-colors duration-200">
            COMMERCIAL <ArrowDown size={14} />
          </a>
          <a href="#residential" data-testid="goto-residential" className="h-12 px-6 border border-white/25 text-xs font-extrabold tracking-[0.18em] flex items-center gap-2 hover:border-pink hover:text-pink transition-colors duration-200">
            RESIDENTIAL <ArrowDown size={14} />
          </a>
          <a href="#inquiry" data-testid="goto-quote" className="h-12 px-6 bg-pink text-white text-xs font-extrabold tracking-[0.18em] flex items-center gap-2 hover:bg-bone hover:text-ink transition-colors duration-200">
            GET A QUOTE <ArrowDown size={14} />
          </a>
        </FadeUp>
      </PageHero>

      <section id="commercial" className="scroll-mt-28 px-5 sm:px-8 py-16 sm:py-24 mt-10 border-t border-white/10" data-testid="commercial-section">
        <SectionHead index="01" title="COMMERCIAL MURALS" />
        <FadeUp className="mt-8 max-w-2xl">
          <p className="font-display text-3xl sm:text-5xl tracking-wide leading-[1.02]">
            BLANK WALLS DON'T SELL ANYTHING<span className="text-pink">.</span>
          </p>
          <p className="mt-5 text-base text-bone/80 leading-relaxed">
            A mural turns your space into the photo your customers post, the landmark people give directions by, the reason somebody walks in instead of walking past. It's not decoration — it's a billboard you own, painted once, working every single day. Offices stop feeling like waiting rooms. Restaurants get a backdrop instead of a wall. Storefronts stop traffic.
          </p>
          <p className="mt-4 text-sm text-smoke leading-relaxed">
            Original artwork, statement design and brand experience in one move — built for the camera and built to last.
          </p>
        </FadeUp>
        <FadeUp><Chips items={COMMERCIAL_SPACES} /></FadeUp>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FadeUp><MuralShot file="commercial-01.jpg" alt="Concept visualization — commercial interior mural by ARTILLERY KDP" testId="concept-commercial-1" /></FadeUp>
          <FadeUp delay={0.08}><MuralShot file="commercial-02.jpg" alt="Concept visualization — commercial exterior mural by ARTILLERY KDP" testId="concept-commercial-2" /></FadeUp>
        </div>
        <FadeUp className="mt-8">
          <a href="#inquiry" data-testid="commercial-quote-cta" className="h-12 px-8 inline-flex items-center gap-2 bg-pink text-white text-sm font-extrabold tracking-[0.18em] hover:bg-bone hover:text-ink transition-colors duration-200">
            GET A COMMERCIAL QUOTE <ArrowDown size={16} />
          </a>
        </FadeUp>
      </section>

      <section id="residential" className="scroll-mt-28 px-5 sm:px-8 py-16 sm:py-24 border-t border-white/10" data-testid="residential-section">
        <SectionHead index="02" title="RESIDENTIAL MURALS" />
        <FadeUp className="mt-8 max-w-2xl">
          <p className="font-display text-3xl sm:text-5xl tracking-wide leading-[1.02]">
            PAINT IS POLITE. A MURAL IS A PERSONALITY<span className="text-pink">.</span>
          </p>
          <p className="mt-5 text-base text-bone/80 leading-relaxed">
            Anyone can pick a beige. A statement wall doesn't match the couch — it makes the room. Game rooms, garages, home gyms, the office you actually live in: bold ARTILLERY KDP artwork scaled to your space turns four walls into the room everyone talks about. It's your house. It should look like you.
          </p>
        </FadeUp>
        <FadeUp><Chips items={RESIDENTIAL_SPACES} /></FadeUp>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FadeUp><MuralShot file="residential-01.jpg" alt="Concept visualization — residential nursery mural by ARTILLERY KDP" testId="concept-residential-1" /></FadeUp>
          <FadeUp delay={0.08}><MuralShot file="residential-02.jpg" alt="Concept visualization — residential grand staircase mural by ARTILLERY KDP" testId="concept-residential-2" /></FadeUp>
        </div>
        <FadeUp className="mt-8">
          <a href="#inquiry" data-testid="residential-quote-cta" className="h-12 px-8 inline-flex items-center gap-2 bg-pink text-white text-sm font-extrabold tracking-[0.18em] hover:bg-bone hover:text-ink transition-colors duration-200">
            START YOUR WALL <ArrowDown size={16} />
          </a>
        </FadeUp>
      </section>

      <section id="inquiry" className="scroll-mt-28 px-5 sm:px-8 py-16 sm:py-24 border-t border-white/10" data-testid="mural-inquiry-section">
        <SectionHead index="03" title="GET A QUOTE" />
        <FadeUp className="mt-4 max-w-md">
          <p className="text-sm text-smoke leading-relaxed">
            Quotes and commissions — tell me about the wall, upload a photo of it, and you'll hear back directly from the artist.
          </p>
        </FadeUp>
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl" data-testid="mural-inquiry-form">
          <div><label htmlFor="m-name" className={labelCls}>NAME *</label>
            <input id="m-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="mural-name" /></div>
          <div><label htmlFor="m-email" className={labelCls}>EMAIL *</label>
            <input id="m-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="mural-email" /></div>
          <div><label htmlFor="m-phone" className={labelCls}>PHONE</label>
            <input id="m-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="mural-phone" /></div>
          <div><label htmlFor="m-type" className={labelCls}>PROJECT TYPE *</label>
            <select id="m-type" value={form.project_type} onChange={set("project_type")} className={inputCls} data-testid="mural-project-type">
              <option>Commercial</option><option>Residential</option>
            </select></div>
          <div><label htmlFor="m-location" className={labelCls}>PROJECT LOCATION</label>
            <input id="m-location" value={form.project_location} onChange={set("project_location")} className={inputCls} data-testid="mural-location" /></div>
          <div><label htmlFor="m-inout" className={labelCls}>INDOOR OR OUTDOOR</label>
            <select id="m-inout" value={form.indoor_outdoor} onChange={set("indoor_outdoor")} className={inputCls} data-testid="mural-indoor-outdoor">
              <option value="">SELECT</option><option>Indoor</option><option>Outdoor</option>
            </select></div>
          <div><label htmlFor="m-dims" className={labelCls}>APPROXIMATE WALL DIMENSIONS</label>
            <input id="m-dims" value={form.wall_dimensions} onChange={set("wall_dimensions")} placeholder='e.g. 12 ft × 9 ft' className={inputCls} data-testid="mural-dimensions" /></div>
          <div><label htmlFor="m-budget" className={labelCls}>TARGET BUDGET</label>
            <input id="m-budget" value={form.budget} onChange={set("budget")} className={inputCls} data-testid="mural-budget" /></div>
          <div className="sm:col-span-2"><label htmlFor="m-style" className={labelCls}>DESIRED STYLE / CONCEPT</label>
            <textarea id="m-style" rows={4} value={form.style_concept} onChange={set("style_concept")}
              className="w-full bg-charcoal border border-white/15 px-4 py-3 text-sm outline-none focus:border-pink" data-testid="mural-style" /></div>
          <div><label htmlFor="m-date" className={labelCls}>DESIRED COMPLETION DATE</label>
            <input id="m-date" type="date" value={form.completion_date} onChange={set("completion_date")} className={`${inputCls} [color-scheme:dark]`} data-testid="mural-date" /></div>
          <div>
            <label htmlFor="m-photo" className={labelCls}>PHOTO OF YOUR WALL</label>
            <label htmlFor="m-photo" data-testid="mural-photo-label"
              className="h-12 border border-dashed border-white/25 flex items-center justify-center gap-2 text-xs font-bold tracking-[0.15em] text-smoke cursor-pointer hover:border-pink hover:text-pink transition-colors duration-200">
              <Upload size={15} /> {photo ? photo.name : "UPLOAD PHOTO"}
            </label>
            <input id="m-photo" type="file" accept="image/*" className="sr-only" data-testid="mural-photo"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" disabled={sending} data-testid="mural-submit"
              className="w-full h-14 bg-bone text-ink text-sm font-extrabold tracking-[0.2em] hover:bg-pink hover:text-white transition-colors duration-200 disabled:opacity-50">
              {sending ? "SENDING…" : "REQUEST A MURAL QUOTE"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
